import { useFormikContext, type FieldInputProps } from 'formik';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  type Resolution,
  type ResolutionConfigItem,
  type ResolutionConfigItemType,
  RESOLUTION_RELATED_DB_ENTITY_TYPES,
  useListProcesses,
  type ResolutionParameter,
  TeamMemberData,
} from '@frontend/api';
import {
  type SelectProps,
  CloseIcon,
  DragHandleIcon,
  HStack,
  IconButton,
  FormControl,
  FormErrorMessage,
  type BoxProps,
  Input,
} from '@frontend/legacy-components';
import { noop } from '@frontend/utils';

import { CUSTOM_DRAGGABLE_ITEM_TYPES } from 'components/customDragLayer/const';
import { useDrag } from 'components/customDragLayer/hooks/useDrag';

import { TeamMemberSelect } from 'modules/common/smartComponents/TeamMemberSelect';
import { excludeLegacyProcesses } from 'modules/quotePartViewer/modals/ConfirmProcessMaterial/ConfirmProcessMaterial';
import Process from 'modules/quotePartViewer/modals/ConfirmProcessMaterial/Process';

import { getGroupId } from 'store/session/selectors';

import { type ResolutionWithId } from '../utils/resolutions';
import { MultiOpDefSelector } from './MultiOpDefSelector';
import {
  ResolutionTypeSelect,
  IconOption,
  IconControl,
  EmptyGroupHeading,
  type ResolutionTypeOption,
} from './ResolutionTypeSelect';

const ProcessResolutionParameter = ({
  param,
  paramIndex,
  fieldProps,
  isDisabled,
  handleResolutionParameterChange,
}: {
  param: ResolutionParameter;
  paramIndex: number;
  fieldProps: Omit<FieldInputProps<string>, 'value'>;
  isDisabled: boolean;
  handleResolutionParameterChange: (
    param: ResolutionParameter,
    paramIndex: number,
  ) => void;
}) => {
  const groupId = useSelector(getGroupId);
  const { data: processes, isFetching: isFetchingProcesses } = useListProcesses(
    { groupId },
  );
  // For Processes
  const process = processes?.find((p) => p.id === param?.value);
  const availableProcesses = excludeLegacyProcesses(
    processes ?? [],
    process ?? null,
  );
  const onChange = useCallback(
    (value: number | undefined) => {
      const processOpt = availableProcesses.find((p) => p.id === value);
      const displayValues = processOpt
        ? { [String(processOpt.id)]: processOpt.name }
        : {};
      handleResolutionParameterChange(
        {
          ...param,
          value: value ?? '',
          // While this is a readonly field on the ResolutionParameter, we want to update
          // this value in the form state for convenient access for the preview
          displayValues,
        },
        paramIndex,
      );
    },
    [availableProcesses, param, paramIndex, handleResolutionParameterChange],
  );
  return (
    <Process
      {...fieldProps}
      disabled={!!isDisabled}
      isClearable={false}
      isLoading={isFetchingProcesses}
      availableProcesses={availableProcesses}
      isMulti={false}
      selectedProcessId={param?.value as number | undefined}
      onChange={onChange}
    />
  );
};

const TeamMemberResolutionParameter = ({
  param,
  paramIndex,
  fieldProps,
  isDisabled,
  handleResolutionParameterChange,
}: {
  param: ResolutionParameter;
  paramIndex: number;
  fieldProps: Omit<FieldInputProps<string>, 'value'>;
  isDisabled: boolean;
  handleResolutionParameterChange: (
    param: ResolutionParameter,
    paramIndex: number,
  ) => void;
}) => {
  const onChange = useCallback(
    (value: TeamMemberData) => {
      handleResolutionParameterChange(
        {
          ...param,
          value: value?.userId,
          // While this is a readonly field on the ResolutionParameter, we want to update
          // this value in the form state for convenient access for the preview
          displayValues: {
            [String(value?.userId)]: `${value?.firstName} ${value?.lastName}`,
          },
        },
        paramIndex,
      );
    },
    [param, paramIndex, handleResolutionParameterChange],
  );
  return (
    <TeamMemberSelect
      {...fieldProps}
      disabled={!!isDisabled}
      isClearable={false}
      isMulti={false}
      mode="assignment"
      value={param?.value as number | undefined}
      onChange={onChange}
    />
  );
};

const OpDefResolutionParameter = ({
  fieldPath,
  param,
  paramIndex,
  fieldProps,
  isDisabled,
  handleResolutionParameterChange,
  onClearResolution,
}: {
  fieldPath: string;
  param: ResolutionParameter;
  paramIndex: number;
  fieldProps: Omit<FieldInputProps<string>, 'value'>;
  isDisabled: boolean;
  handleResolutionParameterChange: (
    param: ResolutionParameter,
    paramIndex: number,
  ) => void;
  onClearResolution: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const handleChange = useCallback(
    (opDefIds: number[], displayValues: Record<string, string>) => {
      const nextDisplayValues = {
        ...(param?.displayValues ?? {}),
        ...displayValues,
      };
      handleResolutionParameterChange(
        {
          ...param,
          value: opDefIds,
          // While this is a readonly field on the ResolutionParameter, we want to update
          // this value in the form state for convenient access for the preview
          displayValues: nextDisplayValues,
        },
        paramIndex,
      );
    },
    [param, paramIndex, handleResolutionParameterChange],
  );

  return (
    <MultiOpDefSelector
      name={fieldPath}
      disabled={!!isDisabled}
      selectedOpDefIds={(param?.value ?? []) as number[]}
      onChange={handleChange}
      onClear={onClearResolution}
      onBlur={fieldProps.onBlur}
    />
  );
};

type ResolutionItemProps = {
  index: number;
  resolutionTypeOptions: NonNullable<
    SelectProps<ResolutionTypeOption>['options']
  >;
  resolutionConfig: ResolutionConfigItem;
  resolution: ResolutionWithId;
  handleResolutionTypeChange: (
    value: ResolutionConfigItemType,
    index: number,
  ) => void;
  onClearResolution?: (index: number) => void;
  onChangeResolution: (value: Resolution, index: number) => void;
  isDisabled?: boolean;
  isFirstResolution: boolean;
};

export const ResolutionItem = ({
  index,
  resolutionConfig,
  resolutionTypeOptions,
  resolution,
  onClearResolution = noop,
  onChangeResolution,
  handleResolutionTypeChange,
  isDisabled,
  isFirstResolution,
  ...props
}: ResolutionItemProps & BoxProps) => {
  const { errors, getFieldProps } = useFormikContext();

  const selectedResolution = useMemo(
    () =>
      resolutionTypeOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === resolution.type),
    [resolutionTypeOptions, resolution.type],
  );

  const handleResolutionParameterChange = useCallback(
    (param: ResolutionParameter, paramIndex: number) => {
      const newResolution = { ...resolution };
      newResolution.parameters[paramIndex] = param;
      onChangeResolution(newResolution, index);
    },
    [onChangeResolution, resolution, index],
  );

  const error = useMemo(() => {
    const startsWith = `resolutions[${index}]`;
    const errorKey = Object.keys(errors).find((key) =>
      key.startsWith(startsWith),
    );
    if (!errorKey) return undefined;
    return errors[errorKey as keyof typeof errors] as string;
  }, [errors, index]);

  // Drag functionality for resolutions
  const dragItem = useMemo(
    () => ({
      draggableItemType: CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_RESOLUTION,
      description: `Resolution ${index + 1}`,
      resolution: {
        resolutionId: resolution.id,
        resolutionIndex: index,
      },
    }),
    [resolution.id, index],
  );

  const [{ isDragging }, connectDragSource] = useDrag({
    item: dragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleClearResolution = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      // to prevent intermittent form submission when clicking clear
      e?.stopPropagation();
      onClearResolution(index);
    },
    [onClearResolution, index],
  );

  const ClearIconButton = useMemo(
    () => (
      <IconButton
        size="sm"
        m={0}
        mt={0.5}
        onClick={handleClearResolution}
        isDisabled={isDisabled}
        aria-label="Remove resolution"
        className="resolution-item-clear-button"
        flexShrink={0}
      >
        <CloseIcon />
      </IconButton>
    ),
    [handleClearResolution, isDisabled],
  );

  return (
    <HStack
      align="stretch"
      alignItems="start"
      spacing={1}
      className="resolution-item-container"
      opacity={isDragging ? 0.5 : 1}
      {...props}
      {...(isFirstResolution && {
        paddingTop: 0,
      })}
    >
      <IconButton
        ref={connectDragSource}
        size="md"
        cursor="grab"
        isDisabled={isDisabled}
        position="relative"
        top={0.5}
      >
        <DragHandleIcon />
      </IconButton>
      <HStack
        align="stretch"
        flexGrow={1}
        spacing={1}
        className="resolution-item-content"
      >
        <ResolutionTypeSelect
          {...getFieldProps(`resolutions.${index}.type`)}
          options={resolutionTypeOptions}
          value={selectedResolution}
          components={{
            Option: IconOption,
            Control: IconControl,
            GroupHeading: EmptyGroupHeading,
          }}
          onChange={(option) => handleResolutionTypeChange(option.value, index)}
          placeholder="Add a resolution"
          isDisabled={isDisabled}
          isClearable={false}
          flexGrow={1}
          className="resolution-item-type-select"
        />
        {/* Custom Label Input */}
        {/* NOTE: this implementation assumes that the custom label options do not have parameters */}
        {resolutionConfig.hasCustomLabel &&
          resolutionConfig.requiredParameters.length === 0 && (
            <HStack flexBasis="125%" alignItems="start">
              <FormControl
                isInvalid={!!error}
                className="resolution-custom-label"
                isRequired
              >
                <Input
                  {...getFieldProps(`resolutions.${index}.customLabel`)}
                  isDisabled={isDisabled}
                  size="md"
                />
                <FormErrorMessage>{error}</FormErrorMessage>
              </FormControl>
              {ClearIconButton}
            </HStack>
          )}
        {!resolutionConfig.hasCustomLabel &&
          resolutionConfig.requiredParameters.length === 0 && (
            <HStack>{ClearIconButton}</HStack>
          )}
        {/* Resolution parameters */}
        {resolutionConfig.requiredParameters.map((reqParam, paramIndex) => {
          const param = resolution.parameters[paramIndex];
          const fieldPath = `resolutions[${index}].parameters[${paramIndex}].value`;
          const key = `${reqParam.name}-${param?.value}-${paramIndex}`;

          // For formik props, don't pass the value prop to the component
          const { value: _, ...fieldProps } = getFieldProps(fieldPath);

          switch (reqParam.relatedDbEntityType) {
            case RESOLUTION_RELATED_DB_ENTITY_TYPES.PROCESS:
              return (
                <HStack flexBasis="125%" alignItems="start" key={key}>
                  <FormControl
                    isInvalid={!!error}
                    className="resolution-param-process"
                  >
                    <ProcessResolutionParameter
                      param={param}
                      paramIndex={paramIndex}
                      fieldProps={fieldProps}
                      isDisabled={!!isDisabled}
                      handleResolutionParameterChange={
                        handleResolutionParameterChange
                      }
                    />
                    <FormErrorMessage>{error}</FormErrorMessage>
                  </FormControl>
                  {ClearIconButton}
                </HStack>
              );
            case RESOLUTION_RELATED_DB_ENTITY_TYPES.USER:
              return (
                <HStack flexBasis="125%" alignItems="start" key={key}>
                  <FormControl
                    isInvalid={!!error}
                    className="resolution-param-user"
                  >
                    <TeamMemberResolutionParameter
                      param={param}
                      paramIndex={paramIndex}
                      fieldProps={fieldProps}
                      isDisabled={!!isDisabled}
                      handleResolutionParameterChange={
                        handleResolutionParameterChange
                      }
                    />
                    <FormErrorMessage>{error}</FormErrorMessage>
                  </FormControl>
                  {ClearIconButton}
                </HStack>
              );
            case RESOLUTION_RELATED_DB_ENTITY_TYPES.OP_DEF:
              return (
                <FormControl
                  isInvalid={!!error}
                  key={reqParam.name}
                  className="resolution-param-opdef"
                  flexGrow={1}
                  flexBasis="125%"
                >
                  <OpDefResolutionParameter
                    fieldPath={fieldPath}
                    param={param}
                    paramIndex={paramIndex}
                    fieldProps={fieldProps}
                    isDisabled={!!isDisabled}
                    handleResolutionParameterChange={
                      handleResolutionParameterChange
                    }
                    onClearResolution={handleClearResolution}
                  />
                  <FormErrorMessage>{error}</FormErrorMessage>
                </FormControl>
              );
            default:
              return null;
          }
        })}
      </HStack>
    </HStack>
  );
};
